import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useGeneralConfig } from '../hooks/useGeneralConfig';
import { useExtensionWorkers } from '../hooks/useExtensionWorkers';
import { BrandScanExtensionMode, GeneralConfig } from '../api/generalConfig';

const BRAND_SCAN_MODE_LABELS: Record<BrandScanExtensionMode, string> = {
  internal_only: 'Internal only (in-cluster scraper)',
  fallback: 'Fallback (extension on auth/CAPTCHA)',
  extension_only: 'Extension only (skip internal search)',
};

export const GeneralSettings: React.FC = () => {
  const { config, isLoading, error, updateConfig, isUpdating } = useGeneralConfig();
  const { data: workersData } = useExtensionWorkers();
  const [browserEngine, setBrowserEngine] = useState<'patchright' | 'playwright'>('patchright');
  const [brandScanMode, setBrandScanMode] = useState<BrandScanExtensionMode>('fallback');
  const [brandScanWaitSeconds, setBrandScanWaitSeconds] = useState(600);
  const [preferredWorkerIds, setPreferredWorkerIds] = useState<string[]>([]);
  const [strictWorkerRouting, setStrictWorkerRouting] = useState(true);

  const workerOptions = useMemo(() => {
    return (workersData?.workers || []).map(w => ({
      id: w.worker_id,
      label: w.display_name || w.worker_id.slice(0, 8),
      status: w.status,
    }));
  }, [workersData?.workers]);

  useEffect(() => {
    if (!config) return;
    if (config.browser_engine) {
      setBrowserEngine(config.browser_engine as 'patchright' | 'playwright');
    }
    if (config.brand_scan_extension_mode) {
      setBrandScanMode(config.brand_scan_extension_mode);
    }
    if (config.brand_scan_extension_wait_seconds != null) {
      setBrandScanWaitSeconds(config.brand_scan_extension_wait_seconds);
    }
    if (Array.isArray(config.brand_scan_preferred_worker_ids)) {
      setPreferredWorkerIds(config.brand_scan_preferred_worker_ids);
    }
    if (config.brand_scan_strict_worker_routing != null) {
      setStrictWorkerRouting(config.brand_scan_strict_worker_routing);
    }
  }, [config]);

  const buildPayload = (): GeneralConfig => ({
    browser_engine: browserEngine,
    brand_scan_extension_mode: brandScanMode,
    brand_scan_extension_wait_seconds: brandScanWaitSeconds,
    brand_scan_preferred_worker_ids: preferredWorkerIds,
    brand_scan_strict_worker_routing: strictWorkerRouting,
  });

  const handleSaveBrowser = () => {
    updateConfig({
      ...(config ?? {}),
      ...buildPayload(),
      browser_engine: browserEngine,
    });
  };

  const handleSaveBrandScan = () => {
    updateConfig({
      ...(config ?? {}),
      ...buildPayload(),
    });
  };

  const browserHasChanges = config && config.browser_engine !== browserEngine;

  const brandScanHasChanges =
    config &&
    (config.brand_scan_extension_mode !== brandScanMode ||
      config.brand_scan_extension_wait_seconds !== brandScanWaitSeconds ||
      JSON.stringify(config.brand_scan_preferred_worker_ids || []) !==
        JSON.stringify(preferredWorkerIds) ||
      config.brand_scan_strict_worker_routing !== strictWorkerRouting);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        General Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure general system settings and service integrations.
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Browser Engine
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose the browser automation engine. Patchright is stealth/undetected (recommended for
            bot-resistant sites). Playwright is the vanilla upstream.
          </Typography>

          <FormControl fullWidth sx={{ minWidth: 200, mt: 1 }} size="small">
            <InputLabel>Browser Engine</InputLabel>
            <Select
              value={browserEngine}
              label="Browser Engine"
              onChange={e => setBrowserEngine(e.target.value as 'patchright' | 'playwright')}
            >
              <MenuItem value="patchright">Patchright (stealth / undetected)</MenuItem>
              <MenuItem value="playwright">Playwright (vanilla)</MenuItem>
            </Select>
          </FormControl>

          {browserHasChanges && (
            <Button
              variant="contained"
              onClick={handleSaveBrowser}
              disabled={isUpdating}
              sx={{ mt: 2 }}
            >
              {isUpdating ? <CircularProgress size={20} /> : 'Save browser engine'}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Brand Threat Scan — Extension Workers
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Controls how the Brand Threat Scan utility searches marketplaces when the in-cluster
            scraper hits auth walls or CAPTCHAs. Store polls async jobs; extension workers claim
            brand-scan work items when enabled.
          </Typography>

          <FormControl fullWidth sx={{ mt: 1 }} size="small">
            <InputLabel>Extension mode</InputLabel>
            <Select
              value={brandScanMode}
              label="Extension mode"
              onChange={e => setBrandScanMode(e.target.value as BrandScanExtensionMode)}
            >
              {(Object.keys(BRAND_SCAN_MODE_LABELS) as BrandScanExtensionMode[]).map(mode => (
                <MenuItem key={mode} value={mode}>
                  {BRAND_SCAN_MODE_LABELS[mode]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Extension wait timeout (seconds)"
            value={brandScanWaitSeconds}
            onChange={e => setBrandScanWaitSeconds(Number(e.target.value) || 600)}
            inputProps={{ min: 60, max: 3600 }}
            sx={{ mt: 2 }}
            helperText="Max time to wait for extension workers before marking searches partial"
          />

          <Autocomplete
            multiple
            size="small"
            options={workerOptions}
            getOptionLabel={opt => `${opt.label} (${opt.status})`}
            value={workerOptions.filter(opt => preferredWorkerIds.includes(opt.id))}
            onChange={(_, selected) => setPreferredWorkerIds(selected.map(s => s.id))}
            renderInput={params => (
              <TextField
                {...params}
                label="Preferred extension workers"
                placeholder="Any online worker"
                sx={{ mt: 2 }}
              />
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />

          <FormControlLabel
            sx={{ mt: 1, display: 'block' }}
            control={
              <Checkbox
                checked={strictWorkerRouting}
                onChange={e => setStrictWorkerRouting(e.target.checked)}
              />
            }
            label="Strict routing — only preferred workers may claim brand scan work"
          />

          {brandScanHasChanges && (
            <Button
              variant="contained"
              onClick={handleSaveBrandScan}
              disabled={isUpdating}
              sx={{ mt: 2 }}
            >
              {isUpdating ? <CircularProgress size={20} /> : 'Save brand scan settings'}
            </Button>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            Preferred workers are highlighted on the System Status page. When strict routing is on
            and workers are selected, only those analysts receive brand scan search work.
          </Alert>
        </CardContent>
      </Card>

      {error != null && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load or save configuration.
        </Alert>
      )}

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Service Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Service authentication tokens are now managed centrally via Kubernetes secrets.
          </Typography>

          <Alert severity="info" sx={{ mt: 2 }}>
            Service authentication is now configured automatically via environment variables. No
            manual token configuration is needed.
          </Alert>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Importer RACE crawl integration
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            By default the scraper polls the importer for pending RACE crawl requests and runs them
            as normal scraping tasks. On the Tasks page, sources are shown as UI (
            <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
              scraper_ui
            </Typography>
            ), Extension (
            <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
              collector_extension
            </Typography>
            ), and Dashboard (
            <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
              importer_crawl_request
            </Typography>
            ). Use the Task source filter to narrow the list.
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Deployment environment variables
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }} color="text.secondary">
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                IMPORTER_CRAWL_CONSUMER_ENABLED
              </Box>{' '}
              — on by default; set to false / 0 / no / off to disable
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                IMPORTER_BASE_URL
              </Box>{' '}
              — defaults to the in-cluster importer service URL (same pattern as Store)
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                API_TOKEN
              </Box>{' '}
              — same secret as Store; used for importer race APIs unless{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                IMPORTER_SERVICE_BEARER_TOKEN
              </Box>{' '}
              is set
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                IMPORTER_CRAWL_POLL_INTERVAL_SECONDS
              </Box>
              ,{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                IMPORTER_CRAWL_KEYWORD_MAX_PAGES
              </Box>
            </Typography>
            <Typography component="li" variant="body2">
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                IMPORTER_CRAWL_MARKETPLACE_MAP
              </Box>{' '}
              — optional JSON override mapping store marketplace codes to scraper definition ids
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
